import { Navigate } from 'react-router-dom'

import { PreloadableRouteObject, TFileToRouteMap } from '~/app/models'
import { getRoutesFromMap } from '~/app/utils/lazy'
import PlusRestrictedRoute from '~/features/auth/components/PlusRestrictedRoute'
import VoipOrgFeatureRoute from '~/features/auth/components/VoipOrgFeatureRoute'
import ProtectedFromPartnerRouteGuard from '~/features/partners/components/protectedFromPartnerRouteGuard'
import { EClientPath } from '~/models/api'
import CampaignCreateEditContainer from '~/pages/campaigns/createEdit'
import OfuCreateCampaignPageLegacy from '~/pages/campaigns/ofuCreateEditLegacy'
import OfuManageCampaignPage from '~/pages/campaigns/ofuManageCampaign'

const inboxImport = () => import('~/pages/inbox')

const basicAccountPortalRoutesFileToRouteMap: TFileToRouteMap = {
  overview: {
    path: EClientPath.overview,
    getImportPromise: () => import('~/pages/overview'),
  },
  convoOptions: {
    path: EClientPath.settingsConvoOptions,
    getImportPromise: () =>
      import('~/features/settings/components/convoOptions'),
  },
  knowledge: {
    path: EClientPath.settingsKnowledge,
    getImportPromise: () => import('~/features/knowledge'),
  },
  // Old v2 set pages and the v3 preview URL (`/settings/knowledge/v3`): redirected into v3.
  individualKnowledgeSet: {
    path: EClientPath.settingsIndividualKnowledgeSet,
    getImportPromise: () =>
      import('~/features/knowledge/v3/components/legacyUrlRedirect'),
  },
  chatbot: {
    path: EClientPath.settingsChatbot,
    getImportPromise: () => import('~/pages/chatbotSettings'),
  },
  email: {
    path: EClientPath.settingsEmail,
    getImportPromise: () => import('~/pages/emailSettings'),
  },
  text: {
    path: EClientPath.settingsText,
    getImportPromise: () => import('~/pages/textSettings'),
  },
  phone: {
    path: EClientPath.settingsPhone,
    getImportPromise: () => import('~/pages/phoneSettings'),
  },
  coreBehavior: {
    path: EClientPath.settingsCoreBehavior,
    getImportPromise: () => import('~/pages/coreBehaviorSettings'),
  },
  notifications: {
    path: EClientPath.settingsNotifications,
    getImportPromise: () => import('~/pages/notificationsSettings'),
  },
  escalations: {
    path: EClientPath.settingsEscalations,
    getImportPromise: () =>
      import('~/features/settings/components/escalations'),
  },
  postConvoSettings: {
    path: EClientPath.settingsPostConvoSettings,
    getImportPromise: () => import('~/pages/postConvoSettings'),
  },
  analysisSettingsV2: {
    path: EClientPath.settingsAnalysisSettingsV2,
    getImportPromise: () => import('~/pages/analysisSettings'),
  },
  inbox: {
    path: EClientPath.inboxMessage,
    getImportPromise: inboxImport,
  },
  inboxConversation: {
    path: EClientPath.inboxConversation,
    getImportPromise: inboxImport,
  },
  contacts: {
    path: EClientPath.contacts,
    getImportPromise: () => import('~/pages/contacts'),
  },
  contactDetails: {
    path: EClientPath.contact,
    getImportPromise: () => import('~/pages/contactDetails'),
  },
  reports: {
    path: EClientPath.reportsDashboard,
    getImportPromise: () => import('~/pages/reports'),
  },
  accountSupport: {
    path: EClientPath.support,
    getImportPromise: () => import('~/pages/accountSupport'),
  },
  plan: {
    path: EClientPath.plan,
    getImportPromise: () => import('~/pages/plan'),
  },
  partnerBilling: {
    path: EClientPath.account,
    getImportPromise: () =>
      import('~/features/partners/components/partnerBilling'),
  },
  privacyPolicy: {
    path: EClientPath.privacyPolicy,
    getImportPromise: () => import('~/pages/privacyPolicy'),
  },
  termsOfUse: {
    path: EClientPath.termsOfUse,
    getImportPromise: () => import('~/pages/termsOfUse'),
  },
  referralsTerms: {
    path: EClientPath.referralsTerms,
    getImportPromise: () => import('~/pages/referrals/referralsTerms'),
  },
  campaignsList: {
    path: EClientPath.campaignList,
    getImportPromise: () => import('~/pages/campaigns/home'),
  },
  campaignTemplates: {
    path: EClientPath.campaignTemplates,
    getImportPromise: () => import('~/pages/campaigns/templates'),
  },
  campaignBuilder: {
    path: EClientPath.campaignBuilder,
    getImportPromise: () => import('~/pages/campaigns/builder'),
  },
  newCampaign: {
    path: EClientPath.campaignSelection,
    getImportPromise: () => import('~/pages/campaigns/typeSelection'),
  },
  manageAssistants: {
    path: EClientPath.manageAssistants,
    getImportPromise: () => import('~/pages/manageAssistants'),
  },
  manageIntegrations: {
    path: EClientPath.manageIntegrations,
    getImportPromise: () => import('~/pages/manageIntegrations'),
  },
  manageIntegrationsAbcFitnessPlatform: {
    path: EClientPath.manageIntegrationsAbcFitnessPlatform,
    getImportPromise: () =>
      import('~/pages/manageIntegrations/abcFitnessPlatform'),
  },
  manageIntegrationsZapier: {
    path: EClientPath.manageIntegrationsZapier,
    getImportPromise: () => import('~/pages/manageIntegrations/zapier'),
  },
  // Routes deprecated, use only for test in local
  // manageScenarios: {
  //   path: EClientPath.manageScenarios,
  //   getImportPromise: () => import('~/pages/scenarios/manageScenarios'),
  // },
  // customScenarioFlow: {
  //   path: EClientPath.customScenarioCreate,
  //   getImportPromise: () =>
  //     import('~/pages/scenarios/createCustomScenarioFlow'),
  // },
  // scenarioDetail: {
  //   path: EClientPath.scenarioCreateEdit,
  //   getImportPromise: () => import('~/pages/scenarios/createEditScenario'),
  // },
  // scenarioActionDetail: {
  //   path: EClientPath.scenarioActionCreateEdit,
  //   getImportPromise: () =>
  //     import('~/pages/scenarios/createEditScenarioAction'),
  // },
  manageScenariosV2: {
    path: EClientPath.manageScenariosV2,
    getImportPromise: () => import('~/pages/scenarios/manageScenarios/v2'),
  },
  manageScenariosActionsV2: {
    path: EClientPath.manageScenariosActions,
    getImportPromise: () => import('~/pages/scenarios/managerActions'),
  },
  customScenarioFlowV2: {
    path: EClientPath.customScenarioV2Create,
    getImportPromise: () =>
      import('~/pages/scenarios/createCustomScenarioFlow/v2'),
  },
  scenarioDetailV2: {
    path: EClientPath.scenarioCreateV2Edit,
    getImportPromise: () => import('~/pages/scenarios/createEditScenario/v2'),
  },
  scenarioActionDetailV2: {
    path: EClientPath.scenarioActionV2CreateEdit,
    getImportPromise: () =>
      import('~/pages/scenarios/createEditScenarioAction/v2'),
  },
  users: {
    path: EClientPath.users,
    getImportPromise: () => import('~/pages/users'),
  },
  liveTransfers: {
    path: EClientPath.liveTransfersScenarios,
    getImportPromise: () => import('~/pages/liveTransfers'),
  },
  liveTransferCreateEdit: {
    path: EClientPath.liveTransferCreateEdit,
    getImportPromise: () => import('~/pages/liveTransfers/createEdit'),
  },
  tcrForm: {
    path: EClientPath.tcrForm,
    getImportPromise: () => import('~/pages/tcrForm'),
  },
  manageBrand: {
    path: EClientPath.manageBrand,
    getImportPromise: () => import('~/pages/brand/manageBrand'),
  },
  manageBrandCreate: {
    path: EClientPath.manageBrandCreate,
    getImportPromise: () => import('~/pages/brand/createBrand'),
  },
  manageBrandDetail: {
    path: EClientPath.manageBrandDetail,
    getImportPromise: () => import('~/pages/brand/brandDetail'),
  },
}

const voipRoutesFileToRouteMap: TFileToRouteMap = {
  voipPhoneNumbers: {
    path: EClientPath.voipPhoneNumbers,
    getImportPromise: () => import('~/pages/voip/phoneNumbers'),
  },
  voipPhoneNumberDetail: {
    path: EClientPath.voipPhoneNumberDetail,
    getImportPromise: () => import('~/pages/voip/phoneNumberDetail'),
  },
  voipDevices: {
    path: EClientPath.voipDevices,
    getImportPromise: () => import('~/pages/voip/devices'),
  },
  voipCalls: {
    path: EClientPath.voipCalls,
    getImportPromise: () => import('~/pages/voip/calls'),
  },
}

const campaignChildRoutesFileToRouteMap: TFileToRouteMap = {
  campaignDetails: {
    path: EClientPath.campaignDetails,
    getImportPromise: () => import('~/features/campaigns/components/details'),
  },
  campaignContacts: {
    path: EClientPath.campaignContacts,
    getImportPromise: () => import('~/features/campaigns/components/contacts'),
  },
  campaignResults: {
    path: EClientPath.campaignResults,
    getImportPromise: () => import('~/features/campaigns/components/results'),
  },
}

const campaignOfuChildRoutesFileToRouteMap: TFileToRouteMap = {
  campaignOfuDetails: {
    path: EClientPath.campaignOfuContacts,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuManageCampaign/campaignContacts/index'),
  },
  campaignOfuContacts: {
    path: EClientPath.campaignOfuPerformance,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuManageCampaign/campaignPerformance/index'),
  },
  campaignOfuResults: {
    path: EClientPath.campaignOfuConfiguration,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuManageCampaign/campaignConfig/index'),
  },
  campaignOfuAssistants: {
    path: EClientPath.campaignOfuAssistants,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuManageCampaign/campaignAssistants/index'),
  },
}

const campaignOfuLegacyChildRoutesFileToRouteMap: TFileToRouteMap = {
  campaignOfuDetailsLegacy: {
    path: EClientPath.campaignOfuDetailsLegacy,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuDetails'),
  },
  campaignOfuContactsLegacy: {
    path: EClientPath.campaignOfuContactsLegacy,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuContacts'),
  },
  campaignOfuResultsLegacy: {
    path: EClientPath.campaignOfuResultsLegacy,
    getImportPromise: () =>
      import('~/features/campaigns/components/ofuResults'),
  },
}

const protectedFromPartnerRoutesFileToRouteMap: TFileToRouteMap = {
  billing: {
    path: EClientPath.billing,
    getImportPromise: () => import('~/pages/billing'),
  },
  referralsInfo: {
    path: EClientPath.referralsInfo,
    getImportPromise: () => import('~/pages/referrals/referralsInfo'),
  },
}

const plusRestrictedRoutesFileToRouteMap: TFileToRouteMap = {
  apiKeys: {
    path: EClientPath.settingsApiKeys,
    getImportPromise: () => import('~/features/settings/components/apiKeys'),
  },
  integrations: {
    path: EClientPath.integrations,
    getImportPromise: () => import('~/pages/integrations'),
  },
}

const accountPortalRoutes: PreloadableRouteObject[] = getRoutesFromMap(
  basicAccountPortalRoutesFileToRouteMap,
)
  .concat({
    element: <ProtectedFromPartnerRouteGuard />,
    children: getRoutesFromMap(protectedFromPartnerRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    element: <PlusRestrictedRoute />,
    children: getRoutesFromMap(plusRestrictedRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    element: <VoipOrgFeatureRoute />,
    children: getRoutesFromMap(voipRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    element: <CampaignCreateEditContainer />,
    path: EClientPath.campaignCreateEdit,
    children: getRoutesFromMap(campaignChildRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    element: <OfuManageCampaignPage />,
    path: EClientPath.campaignOfuManage,
    children: getRoutesFromMap(campaignOfuChildRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    element: <OfuCreateCampaignPageLegacy />,
    path: EClientPath.campaignOfuCreateEditLegacy,
    children: getRoutesFromMap(campaignOfuLegacyChildRoutesFileToRouteMap),
  } as unknown as PreloadableRouteObject)
  .concat({
    path: EClientPath.settingsBase,
    element: <Navigate to={EClientPath.settingsCoreBehavior} replace />,
  } as PreloadableRouteObject)

export default accountPortalRoutes
